import {
  Heart, Smile, Eye, Shield, PiggyBank, Wallet, HeartHandshake,
  CalendarCheck, Gift, Baby, Clock, Laptop, ArrowLeftRight,
  Coffee, Dumbbell, Car, KeyRound, Printer, Users, DoorOpen,
  Building2, Leaf, Mic2, Camera, GraduationCap, Sofa, Sun, Zap, Bike,
  BookOpen, Users2, BadgeCheck, Ticket, Award,
  PartyPopper, Handshake, Megaphone, Target, FileCheck, Scale, Trophy, ClipboardList,
  ShieldCheck, FileBadge, DollarSign, UserCheck, Briefcase, Sparkles,
  type LucideIcon,
} from "lucide-react";

export interface BenefitItem {
  label: string;
  icon: LucideIcon;
}

export interface BenefitGroup {
  title: string;
  items: BenefitItem[];
}

/** The canonical benefits & perks list. Organizations store the labels in `selectedBenefits`. */
const benefitGroups: BenefitGroup[] = [
  {
    title: "Health & Financial Benefits",
    items: [
      { label: "Health Insurance", icon: Heart },
      { label: "Dental Insurance", icon: Smile },
      { label: "Vision Insurance", icon: Eye },
      { label: "Life Insurance", icon: Shield },
      { label: "Retirement Plan / 401k", icon: PiggyBank },
      { label: "FSA / HSA", icon: Wallet },
      { label: "Disability Insurance", icon: HeartHandshake },
      { label: "E&O Insurance Included", icon: ShieldCheck },
      { label: "NAR/CAR Dues Covered", icon: FileBadge },
      { label: "Coaching Stipend", icon: DollarSign },
    ],
  },
  {
    title: "Time Off & Flexibility",
    items: [
      { label: "Unlimited PTO", icon: CalendarCheck },
      { label: "Paid Holidays", icon: Gift },
      { label: "Paid Parental Leave", icon: Baby },
      { label: "Flex Hours", icon: Clock },
      { label: "Remote Work Options", icon: Laptop },
      { label: "Hybrid Schedule", icon: ArrowLeftRight },
    ],
  },
  {
    title: "Office Amenities",
    items: [
      { label: "Free Coffee & Snacks", icon: Coffee },
      { label: "On-site Gym / Fitness Center", icon: Dumbbell },
      { label: "Parking", icon: Car },
      { label: "Secured Access / Key Fob Entry", icon: KeyRound },
      { label: "Printers, Copiers & Scanners", icon: Printer },
      { label: "Conference Rooms", icon: Users },
      { label: "Meeting Rooms", icon: DoorOpen },
      { label: "Private Offices", icon: Building2 },
      { label: "Green Room", icon: Leaf },
      { label: "Podcast Studio", icon: Mic2 },
      { label: "Photography Studio", icon: Camera },
      { label: "Training Room", icon: GraduationCap },
      { label: "Break Room / Lounge", icon: Sofa },
      { label: "Rooftop / Outdoor Space", icon: Sun },
      { label: "EV Charging Stations", icon: Zap },
      { label: "Bike Storage", icon: Bike },
    ],
  },
  {
    title: "Professional Support",
    items: [
      { label: "Transaction Coordinator Included", icon: FileCheck },
      { label: "In-house Legal / Compliance Support", icon: Scale },
      { label: "Admin / Assistant Support", icon: UserCheck },
      { label: "Marketing Coordinator", icon: Briefcase },
      { label: "Dedicated Onboarding Team", icon: Sparkles },
    ],
  },
  {
    title: "Professional Development",
    items: [
      { label: "Professional Development Budget", icon: BookOpen },
      { label: "Mentorship Program", icon: Users2 },
      { label: "Licensing Fee Reimbursement", icon: BadgeCheck },
      { label: "Conference & Event Access", icon: Ticket },
      { label: "Training & Certifications", icon: Award },
    ],
  },
  {
    title: "Culture & Extras",
    items: [
      { label: "Team Events", icon: PartyPopper },
      { label: "Client Appreciation Budget", icon: Handshake },
      { label: "Marketing Support", icon: Megaphone },
      { label: "Lead Generation Tools", icon: Target },
      { label: "Transaction Coordinator Support", icon: FileCheck },
      { label: "In-house Legal / Compliance Support", icon: Scale },
      { label: "Agent Recognition Program", icon: Trophy },
      { label: "Onboarding & Orientation", icon: ClipboardList },
    ],
  },
];

export { benefitGroups };

/** Older profiles stored a few perks under these spellings in `officePerks`. */
const LEGACY_PERK_LABELS: Record<string, string> = {
  "Free coffee/snacks": "Free Coffee & Snacks",
  "On-site gym": "On-site Gym / Fitness Center",
  "Parking": "Parking",
  "Flex hours": "Flex Hours",
  "Remote work options": "Remote Work Options",
  "Team events": "Team Events",
  "Professional development budget": "Professional Development Budget",
  "Client appreciation budget": "Client Appreciation Budget",
};

const strings = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string" && v.trim() !== "") : [];

/**
 * Every benefit an organization selected, with the legacy flags merged in
 * (`hasHealthInsurance`, `hasRetirementPlan`, `officePerks`).
 */
export function selectedBenefitSet(p: Record<string, unknown>, selected: unknown = p.selectedBenefits): Set<string> {
  const all = new Set(strings(selected));
  if (p.hasHealthInsurance) all.add("Health Insurance");
  if (p.hasRetirementPlan) all.add("Retirement Plan / 401k");
  for (const perk of strings(p.officePerks)) all.add(LEGACY_PERK_LABELS[perk] || perk);
  return all;
}

/**
 * The selected benefits that are on the canonical list, in the list's order,
 * once each. Anything not on the list is left out, as on the profile page.
 */
export function selectedBenefitLabels(p: Record<string, unknown>, selected?: unknown): string[] {
  const chosen = selectedBenefitSet(p, selected);
  const out: string[] = [];
  for (const group of benefitGroups) {
    for (const item of group.items) {
      if (chosen.has(item.label) && !out.includes(item.label)) out.push(item.label);
    }
  }
  return out;
}
